/**
 * 手动资产 / 负债的**草稿层**（批 `107` · K1）
 *
 * 依据：`DR-030` · `contract/net-worth-item.ts` · 铁律 24（同一机制一处实现）。
 *
 * ## 🔴 为什么把"草稿 → 请求"抽出来（而不是写在页面里）
 *
 * 三条校验（名称 / 金额 / 币种）各有一个**具体失败句式**，写在 JSX 里就只能靠
 * "渲染出来看看有没有那句中文"来验证 —— 而比断言更糟的是：它**没法成对断言**
 * （"合法输入不报错"这条永远测不到）。抽成纯函数后：合法 → `ok: true`、
 * 每种非法 → 恰好那一条错误，两条都能在零依赖基座里直测（同 `account-draft` / `budget-draft` 范式）。
 *
 * ## 🔴 金额只用共享白名单
 *
 * 「元文本 → 整数分」一律走 `txn-draft.ts` 的 `yuanTextToCentsOrNull()` ——
 * 它内部用的是内核 `yuanTextToCents()`（白名单函数）。全项目**禁浮点**
 * （`/` / `toFixed` / `Math.trunc` 都被 lint 封死），端侧自己写一遍解析必然被拦。
 */

import {
  NetWorthItemKind,
  centsToYuanText,
  type CreateNetWorthItemRequest,
  type NetWorthItemView,
  type UpdateNetWorthItemRequest,
} from '@jiaying/shared';
import { yuanTextToCentsOrNull } from './txn-draft.js';

/** 表单草稿（**受控**：值都在页面里，故可静态断言、也可抽成纯函数） */
export interface NetWorthItemDraft {
  readonly kind: NetWorthItemKind;
  readonly name: string;
  /** 元文本（展示给人看）；提交前才转整数分 */
  readonly amountText: string;
  readonly currency: string;
  readonly note: string;
  readonly hidden: boolean;
}

/** 新建时的空草稿（币种默认**账本本币** —— 由服务端下发的 `baseCurrency` 传入，端侧不自己猜） */
export function emptyItemDraft(kind: NetWorthItemKind, currency: string): NetWorthItemDraft {
  return { kind, name: '', amountText: '', currency, note: '', hidden: false };
}

/** 编辑前回填（🔴 金额回填用内核 `centsToYuanText`，不写 `/100`） */
export function draftFromItem(item: NetWorthItemView): NetWorthItemDraft {
  return {
    kind: item.kind,
    name: item.name,
    amountText: centsToYuanText(item.amount),
    currency: item.currency,
    note: item.note ?? '',
    hidden: item.hidden,
  };
}

/** 名称错误（`null` = 通过） */
export function nameErrorOf(draft: NetWorthItemDraft): string | null {
  const name = draft.name.trim();
  if (name === '') return '请填写名称';
  if (name.length > 40) return '名称不超过 40 个字';
  return null;
}

/**
 * 金额错误（`null` = 通过）。
 *
 * 🔴 **空串是错误**（不是 0）：`amount` 是用户输入的事实值，
 * 把"没填"当成 0 会让净资产里出现一条 ¥0.00 的资产（`notReady` 纪律的同型错误：
 * "没填" 与 "确实是 0" 必须能区分）。
 */
export function amountErrorOf(draft: NetWorthItemDraft): string | null {
  const text = draft.amountText.trim();
  if (text === '') return '请填写金额（不知道金额可以先隐藏，不要填 0）';
  const cents = yuanTextToCentsOrNull(text);
  if (cents === null) return '金额格式不对 —— 请填数字，如 5000 或 5000.00';
  if ((cents as number) < 0) return '金额不得为负 —— 资产 / 负债的方向由「类型」表达，不用负号';
  return null;
}

/** 提交前整体校验（返回**第一条**错误；`null` = 可提交） */
export function draftErrorOf(draft: NetWorthItemDraft): string | null {
  return nameErrorOf(draft) ?? amountErrorOf(draft);
}

/** 草稿 → `POST` 请求体（🔴 调用前必须先过 {@link draftErrorOf}） */
export function toCreateRequest(draft: NetWorthItemDraft): CreateNetWorthItemRequest {
  const cents = yuanTextToCentsOrNull(draft.amountText.trim());
  if (cents === null) throw new RangeError('金额未通过校验，不应构造请求（先调 draftErrorOf）');
  const note = draft.note.trim();
  return {
    kind: draft.kind,
    name: draft.name.trim(),
    amount: cents,
    currency: draft.currency,
    hidden: draft.hidden,
    // 🔴 空备注发 `null`（= 明确没有备注），不发空串 —— 空串会让"没备注"与"备注是空"不可分辨
    note: note === '' ? null : note,
  };
}

/** 草稿 → `PATCH` 请求体（编辑态字段全量发送：本表单就是**整张**，没有"部分改"语义） */
export function toUpdateRequest(draft: NetWorthItemDraft): UpdateNetWorthItemRequest {
  return toCreateRequest(draft);
}
